import type { CollectionRole, Member, Root, TaskRole } from "../../domain/app-types.ts";
import type { Todo } from "../../domain/entities.ts";
import type { StoredEntity } from "../../infrastructure/storage/indexed-db.ts";
import { canCreateTaskInContext, entityVisibleForScopedAccess } from "../navigation/collection-access.ts";

export interface AccessPolicyState {
  roots: Root[];
  activeRootId: string | null;
  activeCollectionId: string | null;
  tasks: Todo[];
  taskRoles: ReadonlyMap<string, TaskRole>;
  collectionRoles: ReadonlyMap<string, CollectionRole>;
}

export function matchesTaskAccessRole(taskId: string, roles: TaskRole[], taskRoles: ReadonlyMap<string, TaskRole>): boolean {
  const role = taskRoles.get(taskId);
  return !!role && roles.includes(role);
}

export function canEditInAccessState(state: AccessPolicyState, taskId?: string): boolean {
  const task = taskId ? state.tasks.find(item => item.id === taskId) : undefined;
  const rootRole = state.roots.find(root => root.id === (task?.rootId || state.activeRootId))?.role || "viewer";
  const collectionId = task?.categoryId || state.activeCollectionId;
  return canCreateTaskInContext(rootRole, collectionId, state.collectionRoles)
    || !!taskId && matchesTaskAccessRole(taskId, ["editor", "assignee"], state.taskRoles);
}

export function canEditTaskInRoot(state: AccessPolicyState, task: Todo, rootId: string): boolean {
  const rootRole = state.roots.find(root => root.id === rootId)?.role || "viewer";
  return rootRole === "owner" || rootRole === "editor"
    || matchesTaskAccessRole(task.id, ["editor", "assignee"], state.taskRoles)
    || !!task.categoryId && state.collectionRoles.get(task.categoryId) === "editor";
}

export function canCommentInAccessState(state: AccessPolicyState, taskId: string): boolean {
  const task = state.tasks.find(item => item.id === taskId);
  return canEditInAccessState(state, taskId)
    || matchesTaskAccessRole(taskId, ["commenter"], state.taskRoles)
    || !!task?.categoryId && state.collectionRoles.get(task.categoryId) === "commenter";
}

export function accessScopeKey(roots: Root[], taskRoles: ReadonlyMap<string, TaskRole>, collectionRoles: ReadonlyMap<string, CollectionRole>): string {
  return [roots.map(root => root.id).sort().join(","), [...taskRoles.keys()].sort().join(","), [...collectionRoles.keys()].sort().join(",")].join("|");
}

export function entityVisibleInAccessScope(entity: StoredEntity, taskOnlyRootIds: ReadonlySet<string>, collectionRootIds: ReadonlySet<string>, taskRoles: ReadonlyMap<string, TaskRole>, collectionRoles: ReadonlyMap<string, CollectionRole>): boolean {
  return entityVisibleForScopedAccess(entity, new Set([...taskOnlyRootIds, ...collectionRootIds]), new Set(taskRoles.keys()), new Set(collectionRoles.keys()));
}

export function membersForRoot(directory: Member[], rootId: string | null): Member[] {
  return directory.filter(member => member.rootId === rootId);
}

export function membersForTask(directory: Member[], rootId: string | null, collectionId: string | null): Member[] {
  const candidates = membersForRoot(directory, rootId).filter(member => !member.collectionId || member.collectionId === collectionId);
  return [...new Map(candidates.map(member => [member.id, member])).values()];
}

export function emailForMember(directory: Member[], id: string): string {
  return directory.find(member => member.id === id)?.email || "Former member";
}
